import React from "react";
import { Bell, LayoutDashboard, ChevronDown, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useNavigation, PageName } from "@/context/NavigationContext";
import { ShoppingCart, User, LogOut, Menu, X, ChevronRight, Navigation } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import SellerAlert from "./SellerAlert";

export interface EnabledFunctionsDto {
    id?: number;
    function?: string;
    type?: string;
    label?: string;
    action?: string;
    parentId?: number;
    enabled?: boolean;
    jsonDetails?: string;
}

const Navbar = () => {
  const { isAuthenticated, logout, enabledMenuFunctions = [], currentUser, currentLang, setCurrentLang, getDictValue } = useAuth();
  const { cart } = useCart();
  const { page, navigateTo, backDestination } = useNavigation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [activeScrollSection, setActiveScrollSection] = useState<"home" | "contact">("home");

  const profileRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const hasSellerAlert = currentUser?.seller && (!currentUser?.iban || currentUser?.iban.trim() === "");
  const itemCount = cart?.cartItems?.length || 0;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (page !== "home") return;

    const contactEl = document.getElementById("contact-section");
    if (!contactEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
          setActiveScrollSection("contact");
        } else if (window.scrollY < (contactEl.offsetTop - 300)) {
          setActiveScrollSection("home");
        }
      },
      { root: null, threshold: [0, 0.1, 0.3, 0.5] }
    );

    observer.observe(contactEl);
    return () => observer.disconnect();
  }, [page]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMenuClick = (action: string) => {
    if (action === "list-category") {
      const isNotConfiguredSeller = !currentUser?.seller || !currentUser?.iban || currentUser?.iban.trim() === "";

      if (!isAuthenticated || isNotConfiguredSeller) {
        setAlertOpen(true);
        return;
      }
    }

    navigateTo(action as PageName);
    setActiveSubmenu(null);
  };

  const getMenuOrder = (item: EnabledFunctionsDto): number => {
    if (!item.jsonDetails) return 999;
    try {
      const details = JSON.parse(item.jsonDetails);
      return details.order ? parseInt(details.order, 10) : 999;
    } catch (e) {
      return 999;
    }
  };

  const getSubmenuItems = (parentId: number | undefined) => {
    if (!parentId) return [];
    return (enabledMenuFunctions as EnabledFunctionsDto[])
      .filter(item => item.parentId === parentId)
      .sort((a, b) => getMenuOrder(a) - getMenuOrder(b));
  };

  const rootMenus = (enabledMenuFunctions as EnabledFunctionsDto[])
    .filter(
      item => (item.parentId === null || item.parentId === undefined) &&
              !(isAuthenticated && item.action === "register")
    )
    .sort((a, b) => getMenuOrder(a) - getMenuOrder(b));

  const toggleLanguage = () => {
    setCurrentLang((prev) => (prev === "ro" ? "en" : "ro"));
  };

  const getMenuLabel = (item: EnabledFunctionsDto): string => {
    const prefix = item.parentId ? "submenu.label" : "menu.label";
    const dictKey = `${prefix}.${item.action}`;
    return getDictValue(dictKey);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "py-2 bg-background/80 backdrop-blur-xl border-b border-white/10 shadow-lg"
          : "py-4 bg-transparent border-b border-transparent"
      }`}
    >
      <SellerAlert
        isOpen={alertOpen}
        isLoggedIn={isAuthenticated}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => {
          setAlertOpen(false);
          navigateTo(isAuthenticated ? "my-account" : "login");
        }}
      />
      <div className="container grid grid-cols-3 items-center">

        {/* LEFT SECTION */}
        <div className="flex items-center gap-6 justify-self-start">
          <div className="relative flex items-center">
            {backDestination && (
              <button
                onClick={() => navigateTo(backDestination)}
                className="absolute -left-12 p-2 rounded-full bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10 hover:border-accent/50 transition-all group shrink-0"
              >
                <Navigation size={18} className="-rotate-90 fill-current transition-all duration-300 group-hover:scale-110 group-hover:text-accent" />
              </button>
            )}

            <div onClick={() => navigateTo("home")} className="flex-shrink-0 cursor-pointer transition-transform active:scale-95">
              <div className="relative">
                <img src="/hangarx-logo.png" alt="Logo" className="h-10 w-auto" />
                <div className="absolute -inset-1 bg-primary/10 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>
        </div>

        {/* MIDDLE SECTION: Desktop Links */}
        <div className="flex justify-center" ref={navRef}>
          <nav className="hidden md:flex items-center gap-1 bg-white/5 p-1 rounded-full border border-white/10 backdrop-blur-md shadow-inner relative">
            {rootMenus.map((item) => {
              const isDropdownContainer = item.action === "children";
              const localizedTitle = getMenuLabel(item);
              const isSubOpen = activeSubmenu === localizedTitle;
              const submenuItems = getSubmenuItems(item.id);
              const hasChildren = submenuItems.length > 0;

              if (isDropdownContainer && hasChildren) {
                return (
                  <div key={item.id} className="relative">
                    <button
                      onClick={() => setActiveSubmenu(isSubOpen ? null : localizedTitle)}
                      className={`px-5 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] transition-all duration-300 flex items-center gap-1.5 ${
                        isSubOpen ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {localizedTitle}
                      <ChevronDown size={12} className={`transition-transform duration-300 ${isSubOpen ? "rotate-180 text-primary" : "text-gray-500"}`} />
                    </button>

                    {/* SUBMENU LAYER DESKTOP */}
                    {isSubOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-[#0B0F1A] border border-white/10 rounded-2xl shadow-2xl p-1.5 animate-in fade-in zoom-in-95 duration-200 z-[60] backdrop-blur-xl">
                        <div className="flex flex-col gap-0.5">
                          {submenuItems.map((child) => (
                            <button
                              key={child.action}
                              onClick={() => handleMenuClick(child.action)}
                              className={`w-full text-left px-4 py-2.5 rounded-xl transition-all text-[11px] font-bold uppercase tracking-wider ${
                                page === child.action ? "bg-primary text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
                              }`}
                            >
                              {getMenuLabel(child)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const isCurrentActive =
                page === "home"
                  ? (item.action === "home" && activeScrollSection === "home") || (item.action === "contact" && activeScrollSection === "contact")
                  : page === item.action;

              return (
                <button
                  key={item.action}
                  onClick={() => handleMenuClick(item.action)}
                  className={`px-6 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
                    isCurrentActive ? "bg-primary text-white shadow-lg shadow-primary/25 scale-105" : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {localizedTitle}
                </button>
              );
            })}
          </nav>
        </div>

        {/* RIGHT SECTION */}
        <div className="flex items-center gap-2 lg:gap-4 justify-self-end">

          {/* 🟢 DESKTOP LANGUAGE SELECTOR BUTTON */}
          <button
            onClick={toggleLanguage}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest active:scale-95"
            title={currentLang === "ro" ? "Switch to English" : "Schimbă în Română"}
          >
            <Languages size={12} className="text-primary" />
            <span>{currentLang}</span>
          </button>

          <button onClick={() => navigateTo("cart")} className="group relative p-3 rounded-full hover:bg-white/5 transition-all active:scale-90">
            <ShoppingCart className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors" />
            {itemCount > 0 && <span className="absolute top-1.5 right-1.5 bg-accent text-white text-[10px] font-black rounded-full h-4 w-4 flex items-center justify-center ring-2 ring-background">{itemCount}</span>}
          </button>

          <div className="h-6 w-px bg-white/10 mx-1 hidden md:block" />

          {isAuthenticated ? (
            <div className="hidden md:flex items-center gap-2 relative" ref={profileRef}>
              <button onClick={() => setProfileOpen(!profileOpen)} className={`group flex items-center gap-2 p-1 rounded-full bg-white/5 border transition-all active:scale-95 ${profileOpen ? "border-primary bg-white/10" : "border-white/10"}`}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white shadow-inner"><User size={14} /></div>
                <span className="hidden lg:block text-[10px] font-bold text-white uppercase tracking-tighter pr-3">Contul meu</span>
                {hasSellerAlert && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                )}
              </button>

              {profileOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-[#0B0F1A] border border-white/10 rounded-2xl shadow-2xl p-2 animate-in fade-in zoom-in-95 duration-200 z-[60] backdrop-blur-xl">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => { navigateTo("my-account"); setProfileOpen(false); }} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-all text-[12px] font-bold uppercase tracking-tight">
                      <User size={16} className="text-blue-500" /> Profilul meu
                      {hasSellerAlert && (
                        <div className="flex items-center gap-1.5 bg-orange-500/10 px-2 py-0.5 rounded-full border border-orange-500/20">
                          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" /><span className="text-[8px] text-orange-500 font-black">IBAN!</span>
                        </div>
                      )}
                    </button>
                    <button onClick={() => { navigateTo("notification-panel"); setProfileOpen(false); }} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-all text-[12px] font-bold uppercase tracking-tight"><Bell size={16} className="text-primary" /> Notificări</button>
                    <button
                      onClick={() => { navigateTo("my-orders"); setProfileOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-all text-[12px] font-bold uppercase tracking-tight"
                    >
                      <ShoppingCart size={16} className="text-emerald-500" /> Comenzile mele
                    </button>
                    <button onClick={() => { navigateTo("dashboard"); setProfileOpen(false); }} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-all text-[12px] font-bold uppercase tracking-tight"><LayoutDashboard size={16} className="text-accent" /> Listările mele</button>
                    <div className="h-px bg-white/5 my-1" />
                    <button onClick={logout} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-all text-[12px] font-bold uppercase tracking-tight"><LogOut size={16} /> Deconectare</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:block">
              <Button size="sm" variant="ghost" className="text-white hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest px-4" onClick={() => navigateTo("login")}>Login</Button>
            </div>
          )}

          <button className="md:hidden p-2 bg-white/5 rounded-lg border border-white/10 text-white transition-colors hover:bg-white/10" onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
      </div>

      {/* 🔴 MOBILE DRAWER */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-x-0 top-[65px] h-[calc(100vh-65px)] bg-[#070a12]/98 backdrop-blur-3xl border-t border-white/5 animate-in slide-in-from-top duration-300 z-50 overflow-y-auto pb-32">
          <div className="container py-6 flex flex-col gap-6">

            {isAuthenticated && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-xl pointer-events-none" />
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white shadow-md">
                    <User size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Autentificat ca</span>
                    <span className="text-xs font-black text-white uppercase tracking-tight truncate max-w-[180px]">
                      {currentUser?.name || "Utilizator HangarX"}
                    </span>
                  </div>
                </div>

                {hasSellerAlert && (
                  <div className="flex items-center justify-between bg-orange-500/10 border border-orange-500/20 rounded-xl px-3 py-2 animate-pulse mt-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full" />
                      <span className="text-[10px] text-orange-400 font-bold uppercase tracking-wider">Configurare IBAN Necesară</span>
                    </div>
                    <button
                      onClick={() => { navigateTo("my-account"); setMobileOpen(false); }}
                      className="text-[9px] font-black uppercase text-orange-400 underline tracking-widest"
                    >
                      Rezolvă
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 🟢 MOBILE LANGUAGE SELECTION COMPONENT */}
            <div className="flex flex-col gap-2">
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 px-2 mb-1">Limbă / Language</span>
              <div className="grid grid-cols-2 gap-2 bg-white/5 p-1 rounded-xl border border-white/10">
                <button
                  onClick={() => setCurrentLang("ro")}
                  className={`py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                    currentLang === "ro" ? "bg-primary text-white" : "text-gray-400"
                  }`}
                >
                  Română (RO)
                </button>
                <button
                  onClick={() => setCurrentLang("en")}
                  className={`py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                    currentLang === "en" ? "bg-primary text-white" : "text-gray-400"
                  }`}
                >
                  English (EN)
                </button>
              </div>
            </div>

            {/* Dynamic Core Navigation Links */}
            <nav className="flex flex-col gap-2">
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 px-2 mb-1">Navigație</span>

              {rootMenus.map((item) => {
                const isDropdownContainer = item.action === "children";
                const localizedTitle = getMenuLabel(item);
                const isSubOpen = activeSubmenu === localizedTitle;
                const submenuItems = getSubmenuItems(item.id);
                const hasChildren = submenuItems.length > 0;

                if (isDropdownContainer && hasChildren) {
                  return (
                    <div key={item.id} className="flex flex-col gap-1 bg-white/[0.02] border border-white/5 rounded-2xl p-1">
                      <button
                        onClick={() => setActiveSubmenu(isSubOpen ? null : localizedTitle)}
                        className={`flex justify-between items-center px-4 py-3 rounded-xl text-[11px] font-bold uppercase tracking-[0.12em] transition-all ${
                          isSubOpen ? "bg-white/5 text-white" : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {localizedTitle}
                        <ChevronDown size={14} className={`transition-transform duration-300 ${isSubOpen ? "rotate-180 text-primary" : "opacity-40"}`} />
                      </button>

                      {isSubOpen && (
                        <div className="flex flex-col gap-1 px-1 pb-1 animate-in slide-in-from-top-2 duration-200">
                          {submenuItems.map((child) => {
                            const isMobileChildActive = page === child.action;
                            return (
                              <button
                                key={child.action}
                                onClick={() => {
                                  handleMenuClick(child.action);
                                  setMobileOpen(false);
                                }}
                                className={`flex justify-between items-center px-4 py-3 rounded-xl text-[11px] font-bold uppercase tracking-[0.1em] transition-all ${
                                  isMobileChildActive
                                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                                    : "bg-white/[0.01] border border-white/5 text-gray-400"
                                }`}
                              >
                                {getMenuLabel(child)}
                                <ChevronRight size={12} className={isMobileChildActive ? "opacity-100 text-white" : "opacity-20"} />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                const isMobileRootActive =
                  page === "home"
                    ? (item.action === "home" && activeScrollSection === "home") || (item.action === "contact" && activeScrollSection === "contact")
                    : page === item.action;

                return (
                  <button
                    key={item.action}
                    onClick={() => {
                      handleMenuClick(item.action);
                      setMobileOpen(false);
                    }}
                    className={`flex justify-between items-center px-5 py-3.5 rounded-xl text-[11px] font-bold uppercase tracking-[0.12em] border transition-all ${
                      isMobileRootActive
                        ? "bg-primary text-white border-primary shadow-lg shadow-primary/20 scale-[1.01]"
                        : "bg-white/5 border-white/5 text-gray-300 hover:text-white"
                    }`}
                  >
                    {localizedTitle}
                    <ChevronRight size={14} className={isMobileRootActive ? "opacity-100" : "opacity-30"} />
                  </button>
                );
              })}
            </nav>
            {/* Account Settings */}
            <div className="flex flex-col gap-2">
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 px-2 mb-1">Setări Cont</span>

              {isAuthenticated ? (
                <div className="grid grid-cols-1 gap-2">
                  {/* 1. Profilul meu */}
                  <button
                    onClick={() => { navigateTo("my-account"); setMobileOpen(false); }}
                    className={`flex items-center gap-3 px-5 py-3.5 rounded-xl border text-[11px] font-bold uppercase tracking-wider transition-all ${
                      page === "my-account" ? "bg-white/10 border-blue-500/30 text-white" : "bg-white/5 border-white/5 text-gray-300"
                    }`}
                  >
                    <User size={16} className="text-blue-500" /> Profilul meu
                  </button>

                  {/* 2. Comenzile mele (Poziționat logic imediat după Profil, la fel ca pe desktop) */}
                  <button
                    onClick={() => { navigateTo("my-orders"); setMobileOpen(false); }}
                    className={`flex items-center justify-between px-5 py-3.5 rounded-xl border text-[11px] font-bold uppercase tracking-wider transition-all ${
                      page === "my-orders" ? "bg-white/10 border-emerald-500/30 text-white" : "bg-white/5 border-white/5 text-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <ShoppingCart size={16} className="text-emerald-500" /> Comenzile mele
                    </div>
                    <ChevronRight size={14} className="opacity-30" />
                  </button>

                  {/* 3. Listările mele */}
                  <button
                    onClick={() => { navigateTo("dashboard"); setMobileOpen(false); }}
                    className={`flex items-center justify-between px-5 py-3.5 rounded-xl border text-[11px] font-bold uppercase tracking-wider transition-all ${
                      page === "dashboard" ? "bg-white/10 border-accent/30 text-white" : "bg-white/5 border-white/5 text-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <LayoutDashboard size={16} className="text-accent" /> Listările mele
                    </div>
                    <ChevronRight size={14} className="opacity-30" />
                  </button>

                  <div className="h-px bg-white/5 my-2" />

                  {/* 4. Deconectare */}
                  <Button
                    variant="destructive"
                    className="rounded-xl py-5 text-[11px] font-bold uppercase tracking-widest gap-3 justify-center border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                    onClick={() => { logout(); setMobileOpen(false); }}
                  >
                    <LogOut size={14} /> Deconectare cont
                  </Button>
                </div>
              ) : (
                <Button
                  className="py-5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-[11px] font-bold uppercase tracking-widest shadow-xl shadow-primary/20"
                  onClick={() => { navigateTo("login"); setMobileOpen(false); }}
                >
                  Autentificare / Log In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;